import { type ComponentProps, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  collapseModals,
  createGroup,
  deleteGroup,
  pushModal,
  updateGroup,
} from 'loot-core/client/actions';
import { send } from 'loot-core/platform/client/fetch';
import { type CategoryGroupEntity } from 'loot-core/types/models';

import { SvgAdd, SvgCheveronDown, SvgEditPencil } from '../../icons/v1';
import { SvgViewHide, SvgViewShow } from '../../icons/v2';
import { SvgDragIcon } from '../../icons/v2/DragIcon';
import { SvgTrash } from '../../icons/v2/Trash';
import { useResponsive } from '../../ResponsiveProvider';
import { theme } from '../../style';
import { Button } from '../common/Button';
import { Menu } from '../common/Menu';
import { Popover } from '../common/Popover';
import { View } from '../common/View';
import {
  type DragState,
  DropHighlight,
  DropHighlightPosContext,
  type OnDragChangeCallback,
  type OnDropCallback,
  useDraggable,
  useDroppable,
} from '../sort';
import { InputCell } from '../table';

import ManageCategoriesCategoryItem from './ManageCategoriesCategoryItem';

type ManageCategoriesCategoryGroupProps = {
  categoryGroup: CategoryGroupEntity;
  indent?: boolean;
  categoryGroups: CategoryGroupEntity[];
  onHideNewGroup?: () => void;
  onShowNewCategory: (groupId: string) => void;
  onHideNewCategory: () => void;
  activeFilter: boolean;
  dragState?: DragState<
    ComponentProps<typeof ManageCategoriesCategoryGroup>['categoryGroup']
  >;
  onDragChange?: OnDragChangeCallback<
    ComponentProps<typeof ManageCategoriesCategoryGroup>['categoryGroup']
  >;
  onReorderCategory?: OnDropCallback;
  onReorderGroup?: OnDropCallback;
  lastCategoryId?: string;
};

const ManageCategoriesCategoryGroup: React.FC<
  ManageCategoriesCategoryGroupProps
> = ({
  categoryGroup,
  indent,
  categoryGroups,
  onHideNewGroup,
  onShowNewCategory,
  onHideNewCategory,
  onDragChange,
  onReorderGroup,
  onReorderCategory,
  activeFilter,
  dragState,
  lastCategoryId,
}) => {
  const temporary = categoryGroup.id === 'new';
  const triggerRef = useRef(null);
  const dispatch = useDispatch();
  const { width, isNarrowWidth } = useResponsive();

  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const dragging = dragState && dragState.item === categoryGroup;

  const { dragRef } = useDraggable({
    type: 'group',
    onDragChange,
    item: categoryGroup,
    canDrag: !editing && !categoryGroup.is_income && !categoryGroup.is_donation && !isNarrowWidth,
  });

  const { dropRef, dropPos } = useDroppable({
    types: 'group',
    id: categoryGroup.id,
    onDrop: onReorderGroup,
    canDrop: dropPos =>
      !(
        (categoryGroup.is_donation && dropPos === 'top') ||
        categoryGroup.is_income
      ) && !isNarrowWidth,
  });

  const { dropRef: catDropRef, dropPos: catDropPos } = useDroppable({
    types: 'expense-category',
    id: categoryGroup.id,
    onDrop: onReorderCategory,
    canDrop: () =>
      !(
        (categoryGroup.is_donation && catDropPos === 'top') ||
        categoryGroup.is_income
      ) && !isNarrowWidth,
  });

  if (
    !categoryGroup ||
    (activeFilter && !categoryGroup.categories?.length && !temporary)
  ) {
    return null;
  }

  const onEditName = isEditing => {
    setEditing(isEditing);
  };

  const onSaveGroup = group => {
    if (group.id === 'new') {
      dispatch(createGroup(group.name, false, false));
      onHideNewGroup?.();
    } else {
      dispatch(updateGroup(group));
    }
  };

  const onDeleteGroup = async groupId => {
    const group = categoryGroups?.find(g => g.id === groupId);

    if (!group) {
      return;
    }

    let mustTransfer = false;
    for (const category of group.categories ?? []) {
      if (await send('must-category-transfer', { id: category.id })) {
        mustTransfer = true;
        break;
      }
    }

    if (mustTransfer) {
      dispatch(
        pushModal('confirm-category-delete', {
          group: groupId,
          onDelete: transferCategory => {
            dispatch(collapseModals('category-group-menu'));
            dispatch(deleteGroup(groupId, transferCategory));
          },
        }),
      );
    } else {
      dispatch(collapseModals('category-group-menu'));
      dispatch(deleteGroup(groupId));
    }
  };

  const getGroupActions = () => {
    return width <= 850 ? (
      <View style={{ marginLeft: 5, flexShrink: 0 }} ref={triggerRef}>
        <Button
          type="bare"
          className="hover-visible"
          onClick={e => {
            e.stopPropagation();
            setMenuOpen(true);
          }}
          style={{ padding: 3 }}
        >
          <SvgCheveronDown width={14} height={14} />
        </Button>
        <Popover
          triggerRef={triggerRef}
          placement="bottom start"
          isOpen={menuOpen}
          onOpenChange={() => setMenuOpen(false)}
          style={{ width: 200 }}
        >
          <Menu
            onMenuSelect={type => {
              if (type === 'rename') {
                onEditName(true);
              } else if (type === 'add-category') {
                onShowNewCategory(categoryGroup.id);
              } else if (type === 'delete') {
                onDeleteGroup(categoryGroup.id);
              } else if (type === 'toggle-visibility') {
                onSaveGroup({
                  ...categoryGroup,
                  hidden: !categoryGroup.hidden,
                });
              }
              setMenuOpen(false);
            }}
            items={[
              { name: 'add-category', text: 'Add category' },
              !categoryGroup.is_income && {
                name: 'toggle-visibility',
                text: categoryGroup.hidden ? 'Show' : 'Hide',
              },
              { name: 'rename', text: 'Rename' },
              !categoryGroup.is_income &&
                !categoryGroup.is_donation && {
                  name: 'delete',
                  text: 'Delete',
                },
            ]}
          />
        </Popover>
      </View>
    ) : (
      <View
        style={{
          flexShrink: 0,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Button
          type="bare"
          onClick={e => {
            e.stopPropagation();
            onShowNewCategory(categoryGroup.id);
          }}
          style={{
            padding: 5,
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <SvgAdd width={12} height={12} /> Add category
        </Button>
        {!categoryGroup.is_income && (
          <Button
            type="bare"
            onClick={e => {
              e.stopPropagation();
              onSaveGroup({
                ...categoryGroup,
                hidden: !categoryGroup.hidden,
              });
            }}
            style={{
              padding: 5,
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            {categoryGroup.hidden ? (
              <SvgViewShow width={14} height={14} />
            ) : (
              <SvgViewHide width={14} height={14} />
            )}
            {categoryGroup.hidden ? 'Show' : 'Hide'}
          </Button>
        )}
        <Button
          type="bare"
          onClick={e => {
            e.stopPropagation();
            onEditName(true);
          }}
          style={{
            padding: 5,
            flexDirection: 'row',
            gap: 4,
            alignItems: 'center',
            fontSize: 12,
          }}
        >
          <SvgEditPencil width={11} height={11} />
          Rename
        </Button>
        {!categoryGroup.is_income && !categoryGroup.is_donation && (
          <Button
            type="bare"
            onClick={e => {
              e.stopPropagation();
              onDeleteGroup(categoryGroup.id);
            }}
            style={{
              padding: 5,
              flexDirection: 'row',
              gap: 4,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <SvgTrash width={16} height={16} style={{ strokeWidth: 2 }} />
            Delete
          </Button>
        )}
      </View>
    );
  };

  const displayed = (
    <View
      innerRef={dragRef}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: 32,
        width: '100%',
        gap: 8,
        backgroundColor: !categoryGroup.is_income
          ? theme.tableRowHeaderBackground
          : theme.tableRowHeaderAlternativeBackground,
        fontWeight: 500,
        opacity: categoryGroup.hidden ? 0.3 : 1,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1,
        }}
      >
        {!categoryGroup.is_income &&
          !categoryGroup.is_donation &&
          !isNarrowWidth && (
            <SvgDragIcon
              width={13}
              height={13}
              style={{ color: theme.pageTextSubdued, cursor: 'grab' }}
            />
          )}
        {categoryGroup.name}
        {!!categoryGroup.is_donation && (
          <img
            style={{ verticalAlign: 'middle', marginLeft: 5 }}
            width={15}
            src="/images/donation-icon.svg"
            alt="donation-icon"
          />
        )}
      </span>
      {getGroupActions()}
    </View>
  );

  return (
    <View>
      {dragState &&
        !dragState.preview &&
        dragState.type === 'group' &&
        !(
          (categoryGroup.is_donation && dropPos === 'top') ||
          categoryGroup.is_income
        ) && (
          <View
            innerRef={dropRef}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: 32 + 41 * categoryGroup.categories.length,
              zIndex: 10000,
            }}
          >
            <DropHighlight pos={dropPos} offset={{ top: 1 }} />
          </View>
        )}
      {dragState &&
        !dragState.preview &&
        dragState.type === 'expense-category' &&
        !(
          (categoryGroup.is_donation && catDropPos === 'top') ||
          categoryGroup.is_income
        ) && <DropHighlight pos={catDropPos} offset={{ top: 1 }} />}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: '0 20px 0 15px',
          height: 32,
          backgroundColor: !categoryGroup.is_income
            ? theme.tableRowHeaderBackground
            : theme.tableRowHeaderAlternativeBackground,
          fontWeight: 500,
          ...(indent ? { paddingLeft: 6 + 15 } : {}),
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            onEditName(false);
            e.stopPropagation();
          }
        }}
      >
        <View
          innerRef={catDropRef}
          style={{
            flex: 1,
            flexDirection: 'row',
            opacity: dragging && !dragState.preview ? 0.3 : 1,
          }}
        >
          <InputCell
            value={categoryGroup.name}
            formatter={() => displayed}
            width="flex"
            exposed={editing || temporary}
            onUpdate={value => {
              const newValue = value.trim();
              if (temporary) {
                if (newValue === '') {
                  onHideNewGroup();
                } else if (newValue !== '') {
                  onSaveGroup({ id: categoryGroup.id, name: newValue });
                }
              } else {
                onSaveGroup({ id: categoryGroup.id, name: newValue });
              }
            }}
            onBlur={() => onEditName(false)}
            style={{ fontWeight: 600, border: 'none' }}
            inputProps={{
              placeholder: temporary ? 'New Group Name' : '',
            }}
          />
        </View>
      </View>
      {!temporary &&
        categoryGroup.categories?.map((cat, idx) => (
          <DropHighlightPosContext.Provider
            key={cat.id || idx}
            value={cat.id === lastCategoryId ? 'last' : null}
          >
            <View
              style={{
                ...(idx !== categoryGroup.categories.length - 1
                  ? {
                      borderBottom: `1px solid ${theme.tableBorder}`,
                    }
                  : {}),
              }}
            >
              <ManageCategoriesCategoryItem
                key={cat.id}
                category={cat}
                indent={indent}
                isCategoryGroupHidden={categoryGroup.hidden}
                onHideNewCategory={onHideNewCategory}
                dragState={dragState}
                onDragChange={onDragChange}
                onReorderCategory={onReorderCategory}
              />
            </View>
          </DropHighlightPosContext.Provider>
        ))}
    </View>
  );
};
export default ManageCategoriesCategoryGroup;
